import { Uuid } from '@edgebox/data-definition-kit';
import { TaxonomyFacet } from '@edgebox/sync-core-data-definitions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import React from 'react';
import { SyncCoreApiContext } from '../../../../contexts/SyncCoreApiContext';
import { AppParamsContext, IAppParams } from '../../../AppParams';
import { useGovernanceTaxonomyManagementContext } from '../GovernanceTaxonomyManagementContext';
import { CATEGORY_ORDER, categoryLabel, facetIcon, taxonomyFacetValue, taxonomyIdentity } from '../GovernanceTaxonomyManagement.utils';
import { ITaxonomyRecommendations, loadOnboardingTaxonomyRecommendations } from '../taxonomySelection.utils';
import { GovernanceTaxonomyAddModal } from './GovernanceTaxonomyAddModal';
import { SkeletonBlock, SkeletonRegion } from './BrandPresenceSkeleton';

export function GovernanceTaxonomySidebar() {
  const {
    groupedTaxonomies,
    selectedTaxonomyIdentity,
    selectTaxonomy,
    unapprovedCountsByTaxonomy,
    loadingTaxonomies,
    taxonomies,
    reloadTaxonomies,
  } = useGovernanceTaxonomyManagementContext();
  const apiContext = React.useContext(SyncCoreApiContext);
  const appParams = React.useContext(AppParamsContext) as IAppParams | null;
  const projectUuid = appParams?.pageOptions?.projectUuid as Uuid | undefined;

  const [showAddModal, setShowAddModal] = React.useState(false);
  const [recommendations, setRecommendations] = React.useState<ITaxonomyRecommendations | undefined>(undefined);
  const recommendationsRequestedRef = React.useRef(false);

  const existingFacets = React.useMemo(
    () => taxonomies.map((taxonomy) => taxonomyFacetValue(taxonomy)).filter((facet): facet is TaxonomyFacet => Boolean(facet)),
    [taxonomies]
  );

  const openAddModal = () => {
    setShowAddModal(true);

    // Best-effort, once per mount: the modal itself only consumes the resulting
    // byte-sized payload and renders a plain list while (or if nothing) resolves.
    if (!recommendationsRequestedRef.current && apiContext?.api) {
      recommendationsRequestedRef.current = true;
      void loadOnboardingTaxonomyRecommendations(apiContext.api).then(setRecommendations);
    }
  };

  return (
    <aside className="w-full shrink-0 border-b border-slate-200/80 bg-white md:w-72 md:border-b-0 md:border-r">
      <div className="p-4 space-y-6">
        {!taxonomies.length && loadingTaxonomies ? (
          <SkeletonRegion label="Loading taxonomies..." className="space-y-6">
            {[4, 3].map((count, group) => (
              <div key={group} aria-hidden="true" className="space-y-2 px-2">
                <SkeletonBlock className="mb-3 h-2.5 w-20" />
                {Array.from({ length: count }, (_unused, row) => (
                  <div key={row} className="flex items-center gap-3 py-1.5">
                    <SkeletonBlock className="h-4 w-4 rounded" />
                    <SkeletonBlock className={`h-3.5 ${row % 2 ? 'w-24' : 'w-32'}`} />
                  </div>
                ))}
              </div>
            ))}
          </SkeletonRegion>
        ) : null}
        {CATEGORY_ORDER.map((category) => {
          const entries = groupedTaxonomies.get(category) || [];
          if (!entries.length) {
            return null;
          }

          return (
            <div key={category}>
              <h3 className="mb-2 px-3 font-eyebrow text-[0.625rem] font-medium uppercase tracking-[0.14em] text-slate-500">
                {categoryLabel(category)}
              </h3>
              <div className="space-y-0.5">
                {entries.map((taxonomy) => {
                  const currentTaxonomyIdentity = taxonomyIdentity(taxonomy);
                  const active = currentTaxonomyIdentity === selectedTaxonomyIdentity;
                  const unapprovedCount = currentTaxonomyIdentity ? unapprovedCountsByTaxonomy[currentTaxonomyIdentity] || 0 : 0;

                  return (
                    <button
                      key={currentTaxonomyIdentity || taxonomy.name}
                      type="button"
                      onClick={() => {
                        void selectTaxonomy(taxonomy);
                      }}
                      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                        active ? 'bg-primary/10 font-semibold text-slate-900' : 'text-slate-600 hover:bg-slate-100/70 hover:text-slate-900'
                      }`}
                    >
                      <FontAwesomeIcon
                        icon={facetIcon(taxonomyFacetValue(taxonomy))}
                        className={`w-4 transition-colors duration-150 ${active ? 'text-primary' : 'text-slate-400'}`}
                      />
                      <span className="text-sm flex items-center gap-2">
                        <span>{taxonomy.name}</span>
                        {unapprovedCount > 0 ? (
                          <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-bold tabular-nums text-white shadow-sm">
                            {unapprovedCount}
                          </span>
                        ) : null}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}

        <button
          type="button"
          onClick={openAddModal}
          className="flex h-9 w-full items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 bg-white px-3 text-sm font-semibold text-slate-600 transition-colors duration-150 hover:border-primary hover:bg-primary/5 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          <FontAwesomeIcon icon={faPlus} />
          <span>Taxonomy</span>
        </button>
      </div>

      {apiContext?.api ? (
        <GovernanceTaxonomyAddModal
          api={apiContext.api}
          show={showAddModal}
          onHide={() => setShowAddModal(false)}
          existingFacets={existingFacets}
          projectUuid={projectUuid}
          recommendations={recommendations}
          onCreated={reloadTaxonomies}
        />
      ) : null}
    </aside>
  );
}
